import { store, getContext, getElement, useLayoutEffect } from '@wordpress/interactivity';

import DOMPurify from 'dompurify';

import './store';

/**
 * Adds feed-specific interactivity to the store.
 */
const { state } = store('mediapress-live', {
  state: {
    get entryIdAttr() {
      const context = getContext();
      return `live-feed-entry-id-${context?.entry?.id}`;
    },
  },
  callbacks: {
    /**
     * Inject the blocks from the REST API response as inner HTML
     */
    renderBlocks: () => {
      const context = getContext();
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useLayoutEffect(() => {
        const newContent = context?.entry?.content?.rendered;

        if (!newContent) {
          return;
        }

        const { ref } = getElement();

        /**
         * We need to allow iFrames to be rendered in the feed,
         * for example for the embed block.
         */
        ref.innerHTML = DOMPurify.sanitize(newContent, {
          ADD_TAGS: ['iframe'],
          ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling'],
        });

        const url = new URL(state.parent.link);
        url.searchParams.append('live-feed-entry-id', context?.entry?.id);

        hydrateShareButton(ref, url.toString(), context?.entry?.title?.rendered);

        window?.twttr?.widgets?.load?.();
      }, [context?.entry?.content?.rendered, context?.entry?.id, context?.entry?.title?.rendered]);
    },
    updatePinnedEntry: () => {
      const clientContext = getContext();
      clientContext.entry = state.pinnedEntry;
    },
    scrollToView: () => {
      const context = getContext();
      const urlParams = new URLSearchParams(window.location.search);
      const entryIdParam = urlParams.get('live-feed-entry-id');

      if (entryIdParam && String(context?.entry?.id) === entryIdParam) {
        const { ref } = getElement();

        if (ref) {
          ref.scrollIntoView({ behavior: 'smooth' });
        }
      }
    },
  },
  actions: {
    expandPinnedEntry: () => {
      state.pinnedEntryIsCollapsed = false;
    },
  },
});

/**
 * Hydrates the share button with the correct URL and title for a dynamically injected entry.
 * Ensures the Share Button block (rendered server-side) keeps its interactivity intact.
 *
 * @param {HTMLElement} ref   - The reference to the element where the share button is located.
 * @param {string}      url   - The url to share.
 * @param {string}      title - The title of the entry to share.
 */
function hydrateShareButton(ref, url, title) {
  if (!ref) return;

  const shareButton = ref
    .closest('.live-feed-item')
    .querySelector('.live-feed-item__actions .wp-block-bigbite-share-button');

  if (!shareButton) {
    return;
  }

  shareButton.dataset.shareUrl = url;
  shareButton.dataset.shareTitle = title;
}
